import { useState, useEffect, useCallback, useRef } from 'react';
import {
  X,
  ZoomIn,
  ZoomOut,
  ChevronLeft,
  ChevronRight,
  RotateCcw,
} from 'lucide-react';

export interface LightboxImage {
  src: string;
  alt?: string;
}

interface ImageLightboxProps {
  images: LightboxImage[];
  currentIndex: number;
  onClose: () => void;
}

const MIN_SCALE = 0.5;
const MAX_SCALE = 5;
const ZOOM_STEP = 0.3;

export default function ImageLightbox({
  images,
  currentIndex,
  onClose,
}: ImageLightboxProps) {
  const [index, setIndex] = useState(currentIndex);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const positionStart = useRef({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  // 切换图片时重置缩放和位置
  useEffect(() => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  }, [index]);

  // 同步外部 currentIndex
  useEffect(() => {
    setIndex(currentIndex);
  }, [currentIndex]);

  const hasPrev = index > 0;
  const hasNext = index < images.length - 1;

  // 🔥 调试日志 — 确认传入的图片数量和当前索引（修复后删除）
  console.log('[ImageLightbox]', {
    imagesCount: images.length,
    index,
    hasPrev,
    hasNext,
    images: images.map(i => i.src.substring(0, 80)),
  });

  const goPrev = useCallback(() => {
    if (hasPrev) setIndex(i => i - 1);
  }, [hasPrev]);

  const goNext = useCallback(() => {
    if (hasNext) setIndex(i => i + 1);
  }, [hasNext]);

  const zoomIn = useCallback(() => {
    setScale(s => Math.min(s + ZOOM_STEP, MAX_SCALE));
  }, []);

  const zoomOut = useCallback(() => {
    setScale(s => Math.max(s - ZOOM_STEP, MIN_SCALE));
  }, []);

  const resetZoom = useCallback(() => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  }, []);

  // 键盘快捷键
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          goPrev();
          break;
        case 'ArrowRight':
          goNext();
          break;
        case '+':
        case '=':
          zoomIn();
          break;
        case '-':
          zoomOut();
          break;
        case '0':
          resetZoom();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, goPrev, goNext, zoomIn, zoomOut, resetZoom]);

  // 🔥 鼠标滚轮缩放 — 使用原生事件监听器 + { passive: false }
  // React 的合成 onWheel 默认使用 passive 监听器，preventDefault() 无效，
  // 导致页面滚动而非缩放图片。必须用原生 addEventListener 并设置 passive: false。
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.deltaY < 0) {
        setScale(s => Math.min(s + ZOOM_STEP * 0.5, MAX_SCALE));
      } else {
        setScale(s => Math.max(s - ZOOM_STEP * 0.5, MIN_SCALE));
      }
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, []);

  // 拖拽平移（缩放状态下）
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (scale <= 1) return;
      e.preventDefault();
      setIsDragging(true);
      dragStart.current = { x: e.clientX, y: e.clientY };
      positionStart.current = { ...position };
    },
    [scale, position]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging) return;
      const dx = e.clientX - dragStart.current.x;
      const dy = e.clientY - dragStart.current.y;
      setPosition({
        x: positionStart.current.x + dx,
        y: positionStart.current.y + dy,
      });
    },
    [isDragging]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // 双击切换缩放
  const handleDoubleClick = useCallback(() => {
    if (scale > 1) {
      resetZoom();
    } else {
      setScale(2.5);
    }
  }, [scale, resetZoom]);

  const currentImage = images[index];
  if (!currentImage) return null;

  return (
    <div
      className="fixed inset-0 bg-black/80 z-[9999] flex items-center justify-center select-none"
      onClick={onClose}
      onMouseUp={handleMouseUp}
    >
      {/* 顶部工具栏 */}
      <div
        className="absolute top-0 left-0 right-0 h-14 flex items-center justify-between px-4 bg-gradient-to-b from-black/60 to-transparent z-10"
        onClick={e => e.stopPropagation()}
      >
        {/* 图片计数 */}
        <div className="text-white/80 text-sm font-medium">
          {images.length > 1 && `${index + 1} / ${images.length}`}
        </div>

        {/* 工具按钮 */}
        <div className="flex items-center gap-1">
          <button
            onClick={zoomOut}
            className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            title="Zoom out (-)"
          >
            <ZoomOut size={20} />
          </button>
          <span className="text-white/70 text-xs min-w-[3rem] text-center">
            {Math.round(scale * 100)}%
          </span>
          <button
            onClick={zoomIn}
            className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            title="Zoom in (+)"
          >
            <ZoomIn size={20} />
          </button>
          <button
            onClick={resetZoom}
            className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            title="Reset zoom (0)"
          >
            <RotateCcw size={18} />
          </button>
          <div className="w-px h-6 bg-white/20 mx-1" />
          <button
            onClick={onClose}
            className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            title="Close (Esc)"
          >
            <X size={22} />
          </button>
        </div>
      </div>

      {/* 左箭头 - 上一张（始终显示，醒目样式） */}
      <button
        className={`absolute left-6 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full transition-all z-20 ${
          hasPrev
            ? 'bg-white text-gray-800 hover:bg-gray-100 cursor-pointer shadow-xl hover:shadow-2xl hover:scale-105'
            : 'bg-gray-600/50 text-gray-400 cursor-not-allowed'
        }`}
        onClick={e => {
          e.stopPropagation();
          if (hasPrev) goPrev();
        }}
        disabled={!hasPrev}
        title="Previous (←)"
      >
        <ChevronLeft size={24} strokeWidth={2.5} />
      </button>

      {/* 右箭头 - 下一张（始终显示，醒目样式） */}
      <button
        className={`absolute right-6 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full transition-all z-20 ${
          hasNext
            ? 'bg-white text-gray-800 hover:bg-gray-100 cursor-pointer shadow-xl hover:shadow-2xl hover:scale-105'
            : 'bg-gray-600/50 text-gray-400 cursor-not-allowed'
        }`}
        onClick={e => {
          e.stopPropagation();
          if (hasNext) goNext();
        }}
        disabled={!hasNext}
        title="Next (→)"
      >
        <ChevronRight size={24} strokeWidth={2.5} />
      </button>

      {/* 图片容器 */}
      <div
        ref={containerRef}
        className="flex items-center justify-center w-full h-full overflow-hidden"
        onClick={e => e.stopPropagation()}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onDoubleClick={handleDoubleClick}
        style={{
          cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
        }}
      >
        <img
          src={currentImage.src}
          alt={currentImage.alt || 'Preview'}
          className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg shadow-2xl pointer-events-none"
          style={{
            transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
            transition: isDragging ? 'none' : 'transform 0.2s ease-out',
          }}
          draggable={false}
        />
      </div>

      {/* 底部缩略图导航（多于 1 张时显示） */}
      {images.length > 1 && (
        <div
          className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 px-3 py-2 bg-black/50 rounded-xl backdrop-blur-sm z-10"
          onClick={e => e.stopPropagation()}
        >
          {images.map((img, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              className={`w-12 h-12 rounded-md overflow-hidden border-2 transition-all flex-shrink-0 ${
                i === index
                  ? 'border-white shadow-lg scale-110'
                  : 'border-transparent opacity-60 hover:opacity-90'
              }`}
            >
              <img
                src={img.src}
                alt=""
                className="w-full h-full object-cover"
                draggable={false}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
